import type { RtRole } from './rt-protocol'

/**
 * API client for the rt endpoints (frozen contract, prefix /api/v1).
 * While S1 (apps/api rt routes) is in flight, tokens beginning with `mock`
 * resolve against a local fixture so the SPA can be exercised end-to-end
 * without the backend (e.g. /join/mock-actor, /join/mock-studio).
 */
export const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000'
export const VERSION_OVERRIDE = import.meta.env.VITE_CF_WORKER_VERSION_OVERRIDE as string | undefined
const BASE = `${API_URL}/api/v1`

export interface RtSessionSummary {
  id: string
  title: string
  status: 'scheduled' | 'live' | 'ended' | 'cancelled'
  project_code: string | null
  frame_rate_fps: number
  scheduled_at?: string | null
  created_at?: string
}

export interface RtLink {
  role: RtRole
  token: string
  url: string
}

export interface CreateSessionResponse {
  session: RtSessionSummary
  links: RtLink[]
}

export interface JoinResponse {
  session: {
    id: string
    title: string
    status: string
    project_code: string | null
    frame_rate_fps: number
  }
  role: RtRole
  display_name: string | null
  livekit: { url: string; token: string }
  /** client-side only: true when served from the local mock fixture */
  mock?: boolean
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers)
  if (VERSION_OVERRIDE) {
    headers.set('Cloudflare-Workers-Version-Overrides', `ohear-api="${VERSION_OVERRIDE}"`)
  }
  const res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: 'include' })
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    /* non-JSON body */
  }
  if (!res.ok) {
    const msg =
      (body as { error?: { message?: string } } | null)?.error?.message ?? `Request failed (${res.status})`
    throw new ApiError(res.status, msg)
  }
  return body as T
}

function jsonHeaders(extra?: Record<string, string>): Record<string, string> {
  return { 'content-type': 'application/json', ...extra }
}

export function createSession(
  body: { title: string; project_code?: string; frame_rate_fps?: number; scheduled_at?: string },
): Promise<CreateSessionResponse> {
  return request<CreateSessionResponse>('/rt/sessions', {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  })
}

export function listSessions(): Promise<{ sessions: RtSessionSummary[] }> {
  return request<{ sessions: RtSessionSummary[] }>('/rt/sessions')
}

export function setSessionStatus(id: string, status: 'live' | 'ended'): Promise<unknown> {
  return request(`/rt/sessions/${id}/status`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ status }),
  })
}

// ---------------------------------------------------------------------------
// Join (public, link-token auth)
// ---------------------------------------------------------------------------

const MOCK_ROLES: RtRole[] = ['studio', 'director', 'engineer', 'actor']

function mockJoin(token: string): JoinResponse {
  const suffix = token.replace(/^mock-?/, '')
  const role: RtRole = (MOCK_ROLES as string[]).includes(suffix) ? (suffix as RtRole) : 'actor'
  return {
    session: {
      id: 'mock-session-0000',
      title: 'Demo Session (offline mock)',
      status: 'live',
      project_code: 'DEMO',
      frame_rate_fps: 24,
    },
    role,
    display_name: `Demo ${role[0]?.toUpperCase()}${role.slice(1)}`,
    livekit: { url: 'ws://127.0.0.1:7880', token: 'mock-livekit-token' },
    mock: true,
  }
}

export async function getJoin(token: string): Promise<JoinResponse> {
  if (token.startsWith('mock')) return mockJoin(token)
  return request<JoinResponse>(`/rt/join/${encodeURIComponent(token)}`)
}

// ---------------------------------------------------------------------------
// Take uploads (public, actor link-token auth — frozen contract)
// ---------------------------------------------------------------------------

export interface RegisterTakeBody {
  take_number: number
  filename: string
  tc_start_samples: number
  sample_rate: number
  bit_depth: number
  channels: number
  sidecar?: Record<string, unknown>
}

export interface RegisterTakeResponse {
  take_id: string
  upload_id: string
  key: string
}

/** POST /rt/join/:token/takes — open the take row + R2 multipart upload. */
export function registerTake(linkToken: string, body: RegisterTakeBody): Promise<RegisterTakeResponse> {
  return request<RegisterTakeResponse>(`/rt/join/${encodeURIComponent(linkToken)}/takes`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
}

/** POST /rt/takes/:take_id/parts/:n/presign — presigned UploadPart URL (15 min). */
export function presignTakePart(
  linkToken: string,
  takeId: string,
  n: number,
  uploadId: string,
): Promise<{ url: string }> {
  return request<{ url: string }>(`/rt/takes/${takeId}/parts/${n}/presign`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-rt-link-token': linkToken },
    body: JSON.stringify({ upload_id: uploadId }),
  })
}

export interface CompleteTakeBody {
  upload_id: string
  parts: Array<{ n: number; etag: string }>
  sha256?: string
  duration_sec?: number
  size_bytes?: number
  sidecar?: Record<string, unknown>
}

/** POST /rt/takes/:take_id/complete — completeMultipartUpload + mark uploaded. */
export function completeTake(
  linkToken: string,
  takeId: string,
  body: CompleteTakeBody,
): Promise<{ ok: true; upload_status: string }> {
  return request(`/rt/takes/${takeId}/complete`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-rt-link-token': linkToken },
    body: JSON.stringify(body),
  })
}

/** POST /rt/takes/:take_id/abort — abortMultipartUpload + upload_status='failed'. */
export function abortTake(
  linkToken: string,
  takeId: string,
  uploadId: string,
): Promise<{ ok: true; upload_status: string }> {
  return request(`/rt/takes/${takeId}/abort`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-rt-link-token': linkToken },
    body: JSON.stringify({ upload_id: uploadId }),
  })
}

// ---------------------------------------------------------------------------
// Network probes used by the pre-flight panel
// ---------------------------------------------------------------------------

/** Median RTT (ms) over `count` GET /health round-trips. */
export async function measureRtt(count = 5): Promise<number> {
  const samples: number[] = []
  for (let i = 0; i < count; i++) {
    const t0 = performance.now()
    await fetch(`${API_URL}/health`, {
      cache: 'no-store',
      headers: VERSION_OVERRIDE
        ? { 'Cloudflare-Workers-Version-Overrides': `ohear-api="${VERSION_OVERRIDE}"` }
        : undefined,
    })
    samples.push(performance.now() - t0)
  }
  samples.sort((a, b) => a - b)
  const mid = samples[Math.floor(samples.length / 2)]
  return Math.round(mid ?? 0)
}

/**
 * Coarse downlink estimate (Mbps) for the v2.9 grade table. Chrome's Network
 * Information API is the only zero-endpoint source we have — it caps the
 * reported value at 10 Mbps for privacy, so `capped:true` means "at least
 * this much". Returns null when the API is unavailable (non-Chromium).
 */
export function measureDownlink(): { mbps: number; capped: boolean } | null {
  const conn = (navigator as { connection?: { downlink?: number } }).connection
  const dl = conn?.downlink
  // Healthy links hover just under the 10 Mbps cap (9.x readings) — treat the
  // whole cap zone as "at least 10" so fast connections don't read as slow.
  if (typeof dl === 'number' && dl > 0) return { mbps: dl, capped: dl >= 9 }
  return null
}

/** Coarse uplink estimate: POST 2 MB to network-test/upload and time the transfer. Returns Mbps. */
export async function measureUplinkMbps(): Promise<number> {
  const payload = new Uint8Array(2 * 1024 * 1024)
  const t0 = performance.now()
  try {
    // network-test/upload drains the body and returns the received byte count, so
    // the full 2 MB travels upstream and the elapsed time reflects real throughput.
    // (The old path POSTed to GET-only /health, which 404s before the body finishes
    // — a false-fast "always PERFECT" reading plus a console error + aborted request.)
    await fetch(`${BASE}/network-test/upload`, {
      method: 'POST',
      body: payload,
      cache: 'no-store',
      headers: {
        'content-type': 'application/octet-stream',
        ...(VERSION_OVERRIDE
          ? { 'Cloudflare-Workers-Version-Overrides': `ohear-api="${VERSION_OVERRIDE}"` }
          : {}),
      },
    })
  } catch {
    return 0 // probe failed to reach the server → no measurable uplink (grades POOR)
  }
  const sec = (performance.now() - t0) / 1000
  if (sec <= 0) return 1000
  return (payload.byteLength * 8) / sec / 1_000_000
}
